import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

type GradientMeshProps = {
  /**
   * "subtle" overlays softly on top of video/imagery (used in the report flow).
   * "vivid" stands on its own over a plain surface (used in the kit flow).
   */
  intensity?: "subtle" | "vivid";
  className?: string;
};

/**
 * Slowly rotating brand-colored radial mesh. Sits behind content as an
 * ambient, calm background animation. Render inside a `relative` container.
 */
export function GradientMesh({ intensity = "subtle", className }: GradientMeshProps) {
  const background =
    intensity === "vivid"
      ? `
        radial-gradient(ellipse 60% 50% at 18% 28%, rgba(93,232,197,0.28) 0%, transparent 68%),
        radial-gradient(ellipse 50% 60% at 82% 18%, rgba(93,232,197,0.18) 0%, transparent 70%),
        radial-gradient(ellipse 55% 50% at 78% 82%, rgba(19,34,56,0.10) 0%, transparent 72%),
        radial-gradient(ellipse 50% 45% at 28% 88%, rgba(93,232,197,0.14) 0%, transparent 70%)
      `
      : `
        radial-gradient(ellipse 60% 50% at 20% 30%, rgba(93,232,197,0.10) 0%, transparent 70%),
        radial-gradient(ellipse 50% 60% at 80% 20%, rgba(93,232,197,0.06) 0%, transparent 70%),
        radial-gradient(ellipse 55% 45% at 60% 80%, rgba(19,34,56,0.05) 0%, transparent 70%)
      `;

  return (
    <div className={cn("pointer-events-none absolute inset-0 overflow-hidden", className)}>
      <motion.div
        className="absolute -left-1/4 -top-1/4 h-[150%] w-[150%]"
        style={{ background }}
        animate={{ rotate: [0, 360] }}
        transition={{ duration: 120, repeat: Infinity, ease: "linear" }}
      />
    </div>
  );
}
